import { Component, OnInit, AfterViewInit, ViewChild, Inject } from '@angular/core';
import { SidebarNavMenuItem } from '../../../decorators/sidebar-nav-menu-item';
import { Requisition } from '../../../models/entities/requisition-model';
import { NavigationEnd, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { ErrorHandlerService } from '../../../decoration-base/error-handler.service';
import { IRequisitionEntityService } from '../../../interfaces/entities/requisition-entity-service-interface';
import { REQUISITION_ENTITY_SERVICE_TOKEN } from '../../../injection-tokens/entity-service-tokens';
import { TrainingScheduleData, TrainingSchedulesData } from '../../../models/wizard-plugin-models/training-schedules-data';
import { filter } from 'rxjs';
import { UserEntityService } from '../../../services/settings/user-entity.service';
import { BusinessProcessEntityService } from '../../../services/settings/businessprocess-entity.service';
import { RightsCases } from '../../../models/rights-cases-enum';
import { Log, IShortLogger } from '../../../services/logger.service';
import { UserPrefsService } from '../../../services/user-prefs.service';
import { AuthService } from '../../../services/auth.service';
import { isParticipant } from '../../../utils/auth.utils';
import { ConfirmDialogService } from '../../../services/confirm-dialog.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../shared/pagination-options';

/** UserPrefs page key for the requisitions info banner. */
const BANNER_PAGE = 'requisitions';

@SidebarNavMenuItem({
  route: '/requisitions',
  faIcon: 'assignment',
  name: 'Requisitions',
  order: 100,
  roles: ['admin', 'sales'],
  businessProcessesName: 'Requisitions'
})
@Component({
  selector: 'app-requisition-list',
  templateUrl: './requisition-list.component.html',
  styleUrl: './requisition-list.component.css'
})
export class RequisitionListComponent implements OnInit, AfterViewInit {
  private readonly logger: IShortLogger<RequisitionListComponent> = Log.create(RequisitionListComponent);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  allRequisitions: any[] = [];
  dataSource = new MatTableDataSource<any>([]);
  readonly displayedColumns = ['enquiryId', 'date', 'customer', 'trainingTitle', 'status', 'actions'];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  public isLoading = false;
  searchTerm = '';
  activeStatuses: string[] = [];

  // ── Banner state ──────────────────────────────────────────────────────────
  bannerVisible = false;
  bannerDismissForever = false;

  showForwardPopup = false;
  selectedRequisition: any = null;
  isVisibleAddRequisitionButton = false;

  readonly KNOWN_STATUSES = [
    { value: 'Draft',                    label: 'Draft',                    badgeClass: 'badge-draft' },
    { value: 'LRC Details Filled',       label: 'LRC Filled',               badgeClass: 'badge-updated' },
    { value: 'Forwarded',               label: 'Forwarded',                badgeClass: 'badge-forwarded' },
    { value: 'CloudSettings Updated',   label: 'Cloud Settings Updated',   badgeClass: 'badge-updated' },
    { value: 'Participants List Updated',label: 'Participants Updated',     badgeClass: 'badge-updated' },
    { value: 'Training Schedule Updated',label: 'Schedule Updated',        badgeClass: 'badge-updated' },
    { value: 'Quota and Cleanup Policy Set', label: 'Policy Set',          badgeClass: 'badge-updated' },
    { value: 'Testing Done By Trainer', label: 'Testing Done',             badgeClass: 'badge-approve' },
  ];

  constructor(
    @Inject(REQUISITION_ENTITY_SERVICE_TOKEN) public requisitionService: IRequisitionEntityService,
    private errorHandler: ErrorHandlerService,
    private businessProcessService: BusinessProcessEntityService,
    private router: Router,
    private userService: UserEntityService,
    private snackBar: MatSnackBar,
    private userPrefs: UserPrefsService,
    protected readonly authService: AuthService,
    private confirmDialog: ConfirmDialogService,
  ) {
    if (this.router?.events) {
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        if (history.state?.refresh) {
          this.loadRequisitions();
        }
      });
    }
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    await this.loadRequisitions();
    this.isLoading = false;
    this.getRightsByCases();

    // Show banner only for operator users who have not permanently dismissed it.
    this.bannerVisible =
      !isParticipant(this.authService.getCurrentUserValue()) &&
      !this.userPrefs.isBannerDismissed(BANNER_PAGE);

    this.dataSource.filterPredicate = (row: any, filterValue: string) => {
      const rd = row?.data?.RequisitionData || {};
      const enquiryId = (rd.enquiryId || rd.docNo || '').toLowerCase();
      const customer = (rd.customerDetails?.name || '').toLowerCase();
      const trainingTitle = this.getTrainingTitle(row).toLowerCase();
      const status = (rd.status || '').toLowerCase();
      return [enquiryId, customer, trainingTitle, status].some(v => v.includes(filterValue));
    };

    this.dataSource.sortingDataAccessor = (row: any, sortHeaderId: string) => {
      const rd = row?.data?.RequisitionData || {};
      switch (sortHeaderId) {
        case 'enquiryId': return rd.enquiryId || rd.docNo || '';
        case 'date':      return rd.docDate ? new Date(rd.docDate).getTime() : 0;
        case 'customer':  return rd.customerDetails?.name || '';
        case 'status':    return rd.status || '';
        case 'trainingTitle': return this.getTrainingTitle(row);
        default: return '';
      }
    };
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  /** Hides the info banner; if "Don't show again" is checked, persists the dismissal. */
  dismissBanner(): void {
    this.bannerVisible = false;
    if (this.bannerDismissForever) {
      this.userPrefs.dismissBanner(BANNER_PAGE);
    }
  }

  loadRequisitions(): Promise<void> {
    return this.errorHandler.tryExecuteAsync(async () => {
      const result = await this.requisitionService.getAllRequisitions();
      this.allRequisitions = result || [];
      this.applyClientFilters();
      this.logger.i('Requisitions loaded:', this.allRequisitions.length);
    }, 'loadRequisitions');
  }

  applyClientFilters(): void {
    let filtered = [...this.allRequisitions];
    if (this.activeStatuses.length > 0) {
      filtered = filtered.filter(r => {
        const rawStatus = (r?.data?.RequisitionData?.status || '').toLowerCase();
        return this.activeStatuses.some(s => {
          const sv = s.toLowerCase();
          // Special case: 'forwarded' chip matches any 'forwarded for ...' status
          if (sv === 'forwarded') return rawStatus.startsWith('forwarded for');
          return rawStatus === sv;
        });
      });
    }
    this.dataSource.data = filtered;
    this.dataSource.filter = this.searchTerm.trim().toLowerCase();
  }

  onSearchChange(): void {
    this.dataSource.filter = this.searchTerm.trim().toLowerCase();
  }

  toggleStatusFilter(status: string): void {
    const idx = this.activeStatuses.findIndex(s => s.toLowerCase() === status.toLowerCase());
    if (idx > -1) {
      this.activeStatuses.splice(idx, 1);
    } else {
      this.activeStatuses.push(status);
    }
    this.applyClientFilters();
  }

  isStatusActive(status: string): boolean {
    const sv = status.toLowerCase();
    return this.activeStatuses.some(s => s.toLowerCase() === sv);
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.activeStatuses = [];
    this.applyClientFilters();
  }

  openAddRequisition(): void {
    this.router.navigate(['/requisitions/create']);
  }

  goToDetail(requisition: any): void {
    this.router.navigate(['/requisitions', requisition.id, 'detail']);
  }

  getTrainingTitle(requisition: any): string {
    const tsd = requisition?.data?.TrainingSchedulesData;
    if (!tsd) return '';
    if (Array.isArray(tsd)) return tsd[0]?.title || tsd[0]?.Title || '';
    return tsd?.TrainingSchedulesData?.[0]?.title
        || tsd?.TrainingSchedulesData?.[0]?.Title
        || '';
  }

  getCustomerInfo(rd: any): string {
    return rd?.customerDetails?.name || '';
  }

  formatDate(date: any): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString();
  }

  getStatusBadgeClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'testing done by trainer') return 'badge-approve';
    if (s.startsWith('forwarded'))       return 'badge-forwarded';
    if (s === 'draft')                   return 'badge-draft';
    return 'badge-updated';
  }

  isPositiveStatus(status: string): boolean {
    return ['APPROVE', 'TRAINERUPDATED'].includes((status || '').toUpperCase());
  }

  openForwardPopup(requisition: any): void {
    this.selectedRequisition = requisition;
    this.showForwardPopup = true;
  }

  onForwarded(result: any): void {
    this.logger.i('Forwarded requisition:', result);
    this.loadRequisitions();
  }

  getRightsByCases(): void {
    const userId = localStorage.getItem('userId') || '';
    this.userService.rightsCasesGetByUserId(userId, [RightsCases.createRequisition]).subscribe({
      next: (result) => {
        this.isVisibleAddRequisitionButton = result?.createRequisition ?? false;
      },
      error: (err) => this.logger.e('Error checking rights:', err)
    });
  }

  // Kept for any template references elsewhere
  getScheduleCount(trainingSchedules: any[]): number {
    return trainingSchedules?.length ?? 0;
  }

  getParticipantsCount(trainingSchedules: any[]): number {
    if (!trainingSchedules) return 0;
    return trainingSchedules.reduce((sum, s) => sum + (s.Participants?.length ?? 0), 0);
  }

  /**
   * Prompts the user for confirmation then archives the requisition.
   * Shows an informational message if the server blocks with 409 (linked active classes).
   */
  archiveRequisition(req: any): void {
    const name = req?.data?.RequisitionData?.enquiryId
      || req?.data?.RequisitionData?.docNo
      || 'this requisition';

    this.confirmDialog.confirmAction(
      'Archive Requisition',
      `Archive "${name}"? Archived requisitions are hidden from the main list.`,
      'warning',
      'Archive',
      'Cancel'
    ).subscribe(confirmed => {
      if (!confirmed) return;

      this.requisitionService.archiveRequisition(req.id).subscribe({
        next: () => {
          this.snackBar.open('Requisition archived.', 'Dismiss', { duration: 3000 });
          this.loadRequisitions();
        },
        error: (err: any) => {
          if (err?.status === 409) {
            this.snackBar.open(
              'Cannot archive: some linked classes are still active. Archive them first.',
              'Dismiss', { duration: 6000 }
            );
          } else {
            this.snackBar.open('Failed to archive requisition.', 'Dismiss', { duration: 4000 });
          }
        }
      });
    });
  }
}
